import type { Type } from '@angular/core';

import type { Observable } from 'rxjs';

import { createProviderToken } from '@core/common-legacy';
import type {
  DetailsDimensionsData,
  ReportDashboardComponent,
  ReportDashboardMetricOptions,
  ReportDetailsData,
  ReportDetailsFragment,
  ReportResults,
  VisualizationDataTypeUnion,
} from '@core/reports-types';
import type {
  DataTableColumn,
  DataTableColumnWithVisibility,
  DialogComponentI,
} from '@core/user-interface-legacy';

export const { inject: injectReportsDetailsDialog, provide: provideReportsDetailsDialog } =
  createProviderToken<new (...args: any[]) => DialogComponentI<any, any>>(
    'REPORTS_DETAILS_DIALOG_TOKEN',
  )();

export type DetailsDataBuilderInput = {
  metric: ReportDashboardMetricOptions;
  component: ReportDashboardComponent;
  data: VisualizationDataTypeUnion;
  rawComponent: ReportDashboardComponent;
  visResponse: ReportResults;
};

export type DetailsDataBuilder = (
  dimensions: DetailsDimensionsData,
  props: DetailsDataBuilderInput,
) => unknown;

export const {
  inject: injectReportsDetailsDataBuilder,
  provide: provideReportsDetailsDataBuilder,
} = createProviderToken<DetailsDataBuilder>('DetailsDataBuilder')();

export interface ReportsDetailsNavigation {
  openResource: (type: string, payload: { id: number | string; [key: string]: unknown }) => void;
  getResourceUrl?: (
    type: string,
    payload: { id: number | string; [key: string]: unknown },
  ) => string | null;
}

export type ReportsDetailsResourceDescriptor = {
  resultsComponent: Type<unknown>;
  columnsFactory?: (meta: unknown) => unknown[];
  rowsTransformer?: (responseData: { details: unknown[]; meta: unknown }) => unknown;
};

export type ReportsDetailsResourceRegistry = Record<string, ReportsDetailsResourceDescriptor>;

export const {
  inject: injectReportsDetailsResourceRegistry,
  provide: provideReportsDetailsResourceRegistry,
} = createProviderToken<ReportsDetailsResourceRegistry>('REPORTS_DETAILS_RESOURCE_REGISTRY')();

export const { inject: injectReportsDetailsNavigation, provide: provideReportsDetailsNavigation } =
  createProviderToken<ReportsDetailsNavigation>('REPORTS_DETAILS_NAVIGATION')();

/**
 * Optional stream of product-specific inputs forwarded to the rendered results component
 * (e.g. `canViewHiredCandidates`, `jobsMap`). Each emitted key is only applied if the active
 * results component declares an input with that name, so providers can emit a superset safely.
 */
export const {
  inject: injectReportsDetailsComponentInputs,
  provide: provideReportsDetailsComponentInputs,
} = createProviderToken<Observable<Record<string, unknown>>>('REPORTS_DETAILS_COMPONENT_INPUTS')();

export type ReportsDetailsDialogState = {
  detailsData: ReportDetailsData;
  resourceType: string;
  dimensions: DetailsDimensionsData;
  metric: ReportDashboardMetricOptions;
  component?: ReportDashboardComponent;
  componentSavedFilters?: unknown;
};

export interface ReportsDetailsColumnsService {
  getTableOpts: (
    resourceType: string,
    meta: unknown,
  ) => { columns: DataTableColumnWithVisibility[]; autoFit: boolean };
  save: (
    opts: { autoFit: boolean; columns: DataTableColumn[] },
    resourceType: string,
    meta: unknown,
  ) => void;
}

export const {
  inject: injectReportsDetailsColumnsService,
  provide: provideReportsDetailsColumnsService,
} = createProviderToken<ReportsDetailsColumnsService>('REPORTS_DETAILS_COLUMNS_SERVICE')();

export type ReportsDetailsPageMeta = {
  pending: boolean;
  loaded: boolean;
  failed: boolean;
  error: unknown;
};

export type ReportsDetailsResponseMeta = {
  skippedCount: number;
  totalCount: number;
  details: string;
  [key: string]: unknown;
};

export interface ReportsDetailsFacadeInterface {
  fetchDetails: (
    detailsData: ReportDetailsData,
    dimensions: DetailsDimensionsData,
    page: number,
  ) => void;
  getDetails: (
    detailsData: ReportDetailsData,
    dimensions: DetailsDimensionsData,
    page: number,
  ) => Observable<{ details: unknown[]; meta: ReportsDetailsResponseMeta }>;
  getDetailsMeta: (
    detailsData: ReportDetailsData,
    dimensions: DetailsDimensionsData,
    page: number,
  ) => Observable<ReportsDetailsPageMeta>;
}

export const { inject: injectReportsDetailsDataFacade, provide: provideReportsDetailsDataFacade } =
  createProviderToken<ReportsDetailsFacadeInterface>('REPORTS_DETAILS_DATA_FACADE')();

export type { ReportDetailsData, ReportDetailsFragment };
